import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useRouteMatch, Switch, Route } from 'react-router-dom';
import { find, first, get } from 'lodash';
import ReactChildType from 'utils/react-child-type';
import SubTab, { SubTabWrapper } from './SubTab';
import { StyledSubTabs } from './StyledComponents';

const DEFAULT_SECTION_NAME = 'SUB_TABS_PANEL';
const ITEM_WIDTH = 375;

const SubTabPanel = ({ subTabs, defaultTabId, onEnter, config, sectionName, id, children }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [activeTabId, setActiveTabId] = useState(null);

  const onSubTabEnter = useCallback(
    (data) => {
      const route = `${match.url}/${data.id}`;
      if (route !== history.location.pathname) history.replace(route);
      setActiveTabId(data.id);
      onEnter && onEnter(data);
    },
    [history, match.url, onEnter],
  );

  useEffect(() => {
    const lastLevel = get(history.location.pathname.match(/\w+$/g), [0], '');
    let currentTab = find(subTabs, ({ id }) => id + '' === lastLevel);
    if (!currentTab && defaultTabId) {
      currentTab = find(subTabs, { id: defaultTabId });
    } else if (!currentTab) {
      currentTab = first(subTabs);
    }
    /*
     * A little delay is needed because sub tabs are used together with tabs
     * on the same page and tabs apply their focus immediately after rendering
     * And this leads to the fact that the focus on sub tabs is lost.
     */
    if (currentTab) setTimeout(() => onSubTabEnter(currentTab), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledSubTabs
        config={config}
        sectionName={sectionName}
        listId={id}
        items={subTabs}
        onEnter={onSubTabEnter}
        itemSize={ITEM_WIDTH}
        renderSize={8}
        startScrollIndex={1}
        itemRender={(props) => {
          const isActive = props.item.id === activeTabId;
          return <SubTab {...props} isActive={isActive} />;
        }}
      />
      <Switch>
        {React.Children.map(children, (child) => {
          if (typeof child !== 'object') return null;
          const { path, children } = child.props;
          return <Route path={`${match.url}/${path}`}>{children}</Route>;
        })}
      </Switch>
    </>
  );
};

SubTabPanel.defaultProps = {
  config: {},
  sectionName: DEFAULT_SECTION_NAME,
  id: 'sub-tabs-panel',
};

SubTabPanel.propTypes = {
  subTabs: PropTypes.array.isRequired,
  defaultTabId: PropTypes.string,
  onEnter: PropTypes.func,
  config: PropTypes.object,
  sectionName: PropTypes.string,
  id: PropTypes.string,
  children: ReactChildType(SubTabWrapper),
};

SubTabPanel.Tab = SubTabWrapper;

export default SubTabPanel;
